import React, { useCallback } from 'react';
import { getGlobalStyles } from '@tanium/coreui-mixins';
import { devWarning, TestIdProps } from '@tanium/coreui-utils';
import { Box } from '@tanium/react-box';
import styled from '@tanium/react-emotion';
import { SimpleTooltip, SimpleTooltipProps } from '@tanium/react-tooltip';
import { css } from '@tanium/style-system';
import { ThemeSpace } from '@tanium/theme-data';
import { ItemDisabledText, ItemFillHover } from './themeDefinitions';

export interface MenuItemProps extends TestIdProps {
  /**
   * The action to take when the menu item is triggered (e.g. when it is clicked).
   */
  onAction: () => void;
  /**
   * The content to display in the menu item.
   */
  children?: React.ReactNode;
  /**
   * Whether the menu item should be disabled.
   */
  disabled?: boolean;
  /**
   * When disabled, a tooltip must be provided explaining why it is disabled.
   */
  disabledTooltip?: SimpleTooltipProps['content'];
}

export const StyledMenuItem = styled('li', {
  shouldForwardProp: (prop) => prop !== 'disabled',
})<{
  disabled?: boolean;
}>(
  getGlobalStyles,
  css({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
    px: ThemeSpace.bordered[1],
  }),
  ({ disabled }) =>
    css(
      disabled
        ? {
            color: ItemDisabledText,
            cursor: 'default',
          }
        : {
            cursor: 'pointer',
            [':hover, :focus' as string]: {
              backgroundColor: ItemFillHover,
            },
          },
    ),
);

/**
 * Item to be displayed within the menu.
 */

export const MenuItem = ({ onAction, children, disabledTooltip, ...props }: MenuItemProps) => {
  devWarning(
    !(props.disabled && !disabledTooltip),
    'A MenuItem was disabled without a disabledTooltip',
  );

  const handleClick = useCallback(() => {
    if (!props.disabled) {
      onAction();
    }
  }, [onAction, props.disabled]);

  const handleKeyDown = useCallback<React.KeyboardEventHandler<HTMLLIElement>>(
    (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        handleClick();
      }
    },
    [handleClick],
  );

  return (
    <SimpleTooltip content={props.disabled ? disabledTooltip : undefined} placement="right">
      {({ ref }) => (
        <StyledMenuItem
          {...props}
          onClick={handleClick}
          tabIndex={props.disabled ? undefined : 0}
          onKeyDown={handleKeyDown}
        >
          <Box ref={ref} width="100%">
            {children}
          </Box>
        </StyledMenuItem>
      )}
    </SimpleTooltip>
  );
};
